import React, { useState, useEffect } from 'react';
import axios from '../lib/axios';
import { 
  Search, 
  Archive, 
  ArrowLeft, 
  Loader2, 
  User, 
  RefreshCcw, 
  ExternalLink,
  Building2,
  Calendar,
  Filter,
  CheckCircle2,
  XCircle
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { resolveMediaUrl } from '../lib/utils';

export default function ArchivedLeads() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [isRestoring, setIsRestoring] = useState(null);

  const fetchArchived = async (searchTerm = '') => {
    setLoading(true);
    try {
      const { data } = await axios.get('/api/leads/archived', {
        params: { search: searchTerm }
      });
      const result = Array.isArray(data) ? data : (data?.data || []);
      setLeads(result);
    } catch (error) {
      console.error('Error fetching archived leads:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Initial fetch
    fetchArchived();
  }, []);

  useEffect(() => {
    if (search.length >= 3 || search.length === 0) {
      const delayDebounceFn = setTimeout(() => {
        fetchArchived(search);
      }, 500);
      return () => clearTimeout(delayDebounceFn);
    }
  }, [search]);

  const handleRestore = async (id) => {
    setIsRestoring(id);
    try {
      await axios.post(`/api/leads/${id}/restore`);
      setLeads((leads || []).filter(l => l.id !== id));
    } catch (error) {
      alert('Error al restaurar el lead');
    } finally {
      setIsRestoring(null);
    }
  };

  return (
    <div className="flex flex-col h-full p-6 md:p-8 gap-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
      
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 bg-white dark:bg-surface-dark p-8 rounded-[2rem] border border-slate-200 dark:border-white/10 shadow-xl relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-primary/5 blur-[100px] -mr-32 -mt-32 rounded-full"></div>
        
        <div className="flex items-center gap-6 relative z-10">
          <Link to="/leads" className="p-3 bg-slate-50 dark:bg-white/5 rounded-2xl hover:bg-primary/10 hover:text-primary transition-all group">
            <ArrowLeft size={20} className="group-hover:-translate-x-1 transition-transform" />
          </Link>
          <div>
            <h1 className="text-3xl font-black text-slate-900 dark:text-white tracking-tighter flex items-center gap-3">
              Archivo de Leads
              <span className="text-[10px] bg-slate-100 dark:bg-white/10 text-slate-500 dark:text-slate-400 px-3 py-1 rounded-full border border-slate-200 dark:border-white/10 uppercase tracking-[0.2em] font-black">
                {leads.length} Registros
              </span>
            </h1>
            <p className="text-slate-500 dark:text-gray-400 mt-1 font-medium italic">Leads que han completado su ciclo y ya no están en el tablero activo.</p>
          </div>
        </div>

        <div className="flex items-center gap-4 relative z-10">
          <div className="relative group">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-primary transition-colors" size={18} />
            <input 
              type="text" 
              placeholder="Buscar por nombre, empresa o email..."
              className="pl-12 pr-6 py-4 bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-2xl focus:outline-none focus:ring-4 focus:ring-primary/10 w-full lg:w-96 transition-all font-medium text-slate-900 dark:text-white shadow-sm"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 bg-white dark:bg-surface-dark rounded-[2.5rem] border border-slate-200 dark:border-white/10 shadow-2xl overflow-hidden flex flex-col">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-50/50 dark:bg-white/[0.02] border-b border-slate-100 dark:border-white/5">
                <th className="px-8 py-6 text-[11px] font-black uppercase tracking-[0.2em] text-slate-400">Lead / Empresa</th>
                <th className="px-8 py-6 text-[11px] font-black uppercase tracking-[0.2em] text-slate-400">Estado Final</th>
                <th className="px-8 py-6 text-[11px] font-black uppercase tracking-[0.2em] text-slate-400">Cierre</th>
                <th className="px-8 py-6 text-[11px] font-black uppercase tracking-[0.2em] text-slate-400">Responsable</th>
                <th className="px-8 py-6 text-[11px] font-black uppercase tracking-[0.2em] text-slate-400 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50 dark:divide-white/5">
              {loading ? (
                <tr>
                  <td colSpan="5" className="px-8 py-32 text-center">
                    <div className="flex flex-col items-center gap-4">
                      <Loader2 className="animate-spin text-primary" size={40} />
                      <p className="text-slate-400 font-bold uppercase tracking-widest text-xs">Consultando archivos históricos...</p>
                    </div>
                  </td>
                </tr>
              ) : leads.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-8 py-32 text-center">
                    <div className="flex flex-col items-center gap-4 opacity-40">
                      <Archive size={64} className="text-slate-300" />
                      <p className="text-slate-500 font-bold uppercase tracking-widest text-sm">No se encontraron leads archivados</p>
                    </div>
                  </td>
                </tr>
              ) : (
                (Array.isArray(leads) ? leads : []).map(lead => (
                  <tr key={lead.id} className="hover:bg-slate-50/50 dark:hover:bg-white/[0.01] transition-colors group">
                    <td className="px-8 py-5">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-2xl bg-primary/5 border border-primary/10 flex items-center justify-center text-primary font-black text-xl shadow-sm overflow-hidden">
                          {lead.avatar ? (
                            <img src={resolveMediaUrl(lead.avatar)} className="w-full h-full object-cover" />
                          ) : (
                            lead.full_name?.charAt(0) || '?'
                          )}
                        </div>
                        <div>
                          <Link to={`/contacts/${lead.id}/360`} className="font-bold text-slate-900 dark:text-white hover:text-primary transition-colors flex items-center gap-2">
                            {lead.full_name}
                            <ExternalLink size={12} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                          </Link>
                          <div className="flex items-center gap-2 text-[11px] text-slate-500 mt-1">
                            <Building2 size={12} />
                            {lead.company || 'Sin Empresa'}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-8 py-5">
                      {lead.pipeline_stage === 'won' ? (
                        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-100 dark:border-emerald-500/20 text-[10px] font-black uppercase tracking-wider">
                          <CheckCircle2 size={12} /> Ganado
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-rose-50 dark:bg-rose-500/10 text-rose-600 dark:text-rose-400 border border-rose-100 dark:border-rose-500/20 text-[10px] font-black uppercase tracking-wider">
                          <XCircle size={12} /> Perdido
                        </span>
                      )}
                    </td>
                    <td className="px-8 py-5">
                      <div className="flex flex-col gap-1">
                        <span className="text-xs font-bold text-slate-700 dark:text-slate-300 flex items-center gap-1.5">
                          <Calendar size={12} className="text-slate-400" />
                          {new Date(lead.updated_at).toLocaleDateString()}
                        </span>
                        <span className="text-[10px] text-slate-400 font-medium">
                          {new Date(lead.updated_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                    </td>
                    <td className="px-8 py-5">
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-xl bg-slate-100 dark:bg-white/10 flex items-center justify-center overflow-hidden border border-slate-200 dark:border-white/10">
                          {lead.owner?.avatar ? (
                            <img src={resolveMediaUrl(lead.owner.avatar)} className="w-full h-full object-cover" />
                          ) : (
                            <User size={14} className="text-slate-400" />
                          )}
                        </div>
                        <span className="text-xs font-bold text-slate-600 dark:text-slate-400">
                          {lead.owner?.name || 'No asignado'}
                        </span>
                      </div>
                    </td>
                    <td className="px-8 py-5 text-right">
                      <button 
                        onClick={() => handleRestore(lead.id)}
                        disabled={isRestoring === lead.id}
                        className="p-3 bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 rounded-2xl hover:bg-indigo-600 hover:text-white transition-all shadow-sm border border-indigo-100 dark:border-indigo-500/20 disabled:opacity-50 group"
                        title="Restaurar a Pipeline Activo"
                      >
                        {isRestoring === lead.id ? (
                          <Loader2 size={18} className="animate-spin" />
                        ) : (
                          <RefreshCcw size={18} className="group-hover:rotate-180 transition-transform duration-500" />
                        )}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Footer info */}
        <div className="px-8 py-4 bg-slate-50/50 dark:bg-white/[0.01] border-t border-slate-100 dark:border-white/5 flex items-center justify-between text-[10px] font-bold text-slate-400 uppercase tracking-widest">
          <div>Mostrando {leads.length} resultados archivados</div>
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-primary animate-pulse"></div>
            Base de datos sincronizada
          </div>
        </div>
      </div>
    </div>
  );
}
